'use client'

/**
 * Theme Studio — craft presets + custom desk systems.
 * Free: presets only. Starter: save up to 3. Pro: expanded library.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { message } from 'antd'
import { useTheme } from '@/shared/theme/provider/ThemeProvider'
import { listThemeCatalog, type PresetThemeId } from '@/shared/theme/catalog'
import {
  PRESET_TOKENS,
} from '@/shared/theme/systems/presets'
import {
  type DeskTokenSet,
  tokensFromMechagora,
} from '@/shared/theme/systems/desk-tokens'
import type {
  ClientThemePreference,
  CustomDeskSystem,
} from '@/shared/theme/client/preference-types'
import { systemRefKey } from '@/shared/theme/client/preference-types'
import { writeLocalThemeCache } from '@/shared/theme/persistence/local-cache'
import styles from './ThemeStudio.module.css'

const TOKEN_LABELS: { key: keyof DeskTokenSet; label: string }[] = [
  { key: 'canvas', label: 'Main canvas' },
  { key: 'container', label: 'Panels' },
  { key: 'elevated', label: 'Elevated surfaces' },
  { key: 'textPrimary', label: 'Primary text' },
  { key: 'textSecondary', label: 'Secondary labels' },
  { key: 'grid', label: 'Grid / dividers' },
  { key: 'bull', label: 'Bullish / profit' },
  { key: 'bear', label: 'Bearish / risk' },
  { key: 'primary', label: 'Accent / focus' },
]

type Limits = { deskThemeSync: boolean; maxCustomDeskThemes: number }

function ensureHash(hex: string): string {
  const h = hex.trim()
  if (!h) return '#121620'
  return h.startsWith('#') ? h : `#${h}`
}

export function ThemeStudio() {
  const { mode, setMode, setColorTheme, colorTheme, type } = useTheme()
  const [pref, setPref] = useState<ClientThemePreference | null>(null)
  const [limits, setLimits] = useState<Limits>({
    deskThemeSync: false,
    maxCustomDeskThemes: 0,
  })
  const [name, setName] = useState('My espresso desk')
  const [tokens, setTokens] = useState<DeskTokenSet>(() => tokensFromMechagora('dark'))
  const [editId, setEditId] = useState<string | undefined>()
  const [busy, setBusy] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/client/theme', {
        credentials: 'same-origin',
        cache: 'no-store',
      })
      if (!res.ok) {
        setLoaded(true)
        return
      }
      const data = await res.json()
      if (data.preference) setPref(data.preference)
      if (data.limits) setLimits(data.limits)
    } catch {
      /* unauthenticated studio still edits local presets */
    } finally {
      setLoaded(true)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const canSave = limits.maxCustomDeskThemes > 0
  const canSync = limits.deskThemeSync
  const customCount = pref?.customs.length ?? 0

  const applyPreset = (id: PresetThemeId) => {
    setColorTheme(id)
    const pack = PRESET_TOKENS[id]
    const t = type === 'dark' ? pack.dark : pack.light
    setTokens(t)
    setEditId(undefined)
    setName(`Copy of ${listThemeCatalog().find((c) => c.id === id)?.name ?? id}`)
    writeLocalThemeCache({ mode, colorTheme: id })
    if (canSync) {
      void fetch('/api/client/theme', {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: `preset:${id}`, mode }),
      })
    }
  }

  const applyCustom = (c: CustomDeskSystem) => {
    setTokens(c.tokens)
    setName(c.name)
    setEditId(c.id)
    // Apply CSS vars via closest preset shell + live override
    setColorTheme(c.basedOn ?? 'mechagora')
    setMode(c.preferredMode)
    if (typeof document !== 'undefined') {
      const root = document.documentElement
      root.style.setProperty('--color-bg-layout', c.tokens.canvas)
      root.style.setProperty('--color-background', c.tokens.canvas)
      root.style.setProperty('--color-bg-container', c.tokens.container)
      root.style.setProperty('--color-card', c.tokens.container)
      root.style.setProperty('--color-bg-elevated', c.tokens.elevated)
      root.style.setProperty('--color-text-primary', c.tokens.textPrimary)
      root.style.setProperty('--color-text-secondary', c.tokens.textSecondary)
      root.style.setProperty('--color-border', c.tokens.grid)
      root.style.setProperty('--color-success', c.tokens.bull)
      root.style.setProperty('--color-error', c.tokens.bear)
      root.style.setProperty('--color-primary', c.tokens.primary)
    }
    if (canSync) {
      void fetch('/api/client/theme', {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          active: `custom:${c.id}`,
          mode: c.preferredMode,
        }),
      })
    }
  }

  const onToken = (key: keyof DeskTokenSet, value: string) => {
    setTokens((prev) => ({ ...prev, [key]: ensureHash(value) }))
  }

  const saveCustom = async () => {
    if (!canSave) {
      message.info('Starter saves named desk systems to your account')
      return
    }
    setBusy(true)
    try {
      const res = await fetch('/api/client/theme', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: editId,
          name,
          tokens,
          preferredMode: type === 'light' ? 'light' : 'dark',
          basedOn: isPreset(colorTheme) ? colorTheme : 'mechagora',
        }),
      })
      const data = await res.json()
      if (!res.ok) {
        message.error(data.message || data.error || 'Save failed')
        return
      }
      setPref(data.preference)
      const last = data.preference.customs?.slice(-1)[0] as CustomDeskSystem | undefined
      if (last) setEditId(last.id)
      message.success('Desk system saved to your account')
    } finally {
      setBusy(false)
    }
  }

  const removeCustom = async (id: string) => {
    setBusy(true)
    try {
      const res = await fetch(`/api/client/theme?id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
        credentials: 'same-origin',
      })
      const data = await res.json()
      if (res.ok) {
        setPref(data.preference)
        message.success('Removed')
      }
    } finally {
      setBusy(false)
    }
  }

  const swatches = useMemo(
    () => [tokens.canvas, tokens.container, tokens.primary, tokens.bull, tokens.bear],
    [tokens]
  )

  return (
    <div className={styles.studio}>
      <div className={styles.panel}>
        <h2 className={styles.title}>Theme Studio</h2>
        <p className={styles.sub}>
          Craft presets for everyone. Starter saves named systems to your account
          so the desk looks the same every login.
        </p>

        <p className={styles.meta}>
          {loaded
            ? canSave
              ? `Saved systems ${customCount} / ${limits.maxCustomDeskThemes}`
              : 'Free: craft presets on this device'
            : 'Loading…'}
        </p>

        <div className={styles.actions}>
          {listThemeCatalog().map((p) => (
            <button
              key={p.id}
              type="button"
              className={styles.btn}
              onClick={() => applyPreset(p.id)}
            >
              {p.name}
            </button>
          ))}
        </div>

        <div className={styles.field} style={{ marginTop: 14 }}>
          <label htmlFor="desk-name">System name</label>
          <input
            id="desk-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{
              padding: '8px 10px',
              borderRadius: 8,
              border: '1px solid var(--color-border)',
              background: 'var(--color-surface-secondary)',
              color: 'var(--color-text-primary)',
            }}
          />
        </div>

        {TOKEN_LABELS.map(({ key, label }) => (
          <div key={key} className={styles.field}>
            <label htmlFor={`tok-${key}`}>{label}</label>
            <div className={styles.row}>
              <input
                id={`tok-${key}`}
                type="color"
                value={normalizeColorInput(tokens[key])}
                onChange={(e) => onToken(key, e.target.value)}
              />
              <input
                type="text"
                value={tokens[key]}
                onChange={(e) => onToken(key, e.target.value)}
              />
            </div>
          </div>
        ))}

        <div className={styles.actions}>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={busy || !canSave}
            onClick={() => void saveCustom()}
          >
            {editId ? 'Update system' : 'Save to account'}
          </button>
          <button
            type="button"
            className={styles.btn}
            onClick={() => {
              setEditId(undefined)
              setTokens(PRESET_TOKENS.mechagora.dark)
              setName('My espresso desk')
            }}
          >
            Reset editor
          </button>
        </div>

        {!canSave && (
          <div className={styles.gate}>
            <h3>Your agents deserve your desk</h3>
            <p>
              Free includes Mechagora craft presets. Starter saves named color
              systems to your account — up to 3 — so the desk is yours every login.
            </p>
            <a className={`${styles.btn} ${styles.btnPrimary}`} href="/settings/billing">
              Unlock saved desk systems
            </a>
          </div>
        )}

        {canSave && pref && pref.customs.length > 0 && (
          <>
            <p className={styles.sub} style={{ marginTop: 16 }}>
              Your systems
            </p>
            <ul className={styles.customs}>
              {pref.customs.map((c) => (
                <li key={c.id}>
                  <button type="button" onClick={() => applyCustom(c)}>
                    {c.name}
                  </button>
                  <button
                    type="button"
                    className={styles.btn}
                    style={{ marginTop: 4 }}
                    onClick={() => void removeCustom(c.id)}
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className={`${styles.panel} ${styles.preview}`}>
        <h2 className={styles.title}>Live preview</h2>
        <p className={styles.sub}>
          Mode {mode} · resolved {type}
          {pref?.active
            ? ` · active ${systemRefKey(pref.active)}`
            : ''}
        </p>
        <div className={styles.swatchStrip} aria-hidden>
          {swatches.map((c, i) => (
            <span key={i} style={{ background: c }} />
          ))}
        </div>
        <div
          style={{
            padding: 16,
            borderRadius: 8,
            background: tokens.canvas,
            color: tokens.textPrimary,
            border: `1px solid ${tokens.grid}`,
          }}
        >
          <div style={{ fontSize: 11, color: tokens.textSecondary, marginBottom: 8 }}>
            SESSION · SIM
          </div>
          <div style={{ fontSize: 28, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            67,420.50
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 13 }}>
            <span style={{ color: tokens.bull }}>+412.20 bull</span>
            <span style={{ color: tokens.bear }}>-180.40 bear</span>
            <span style={{ color: tokens.primary }}>accent</span>
          </div>
          <div
            style={{
              marginTop: 14,
              padding: 12,
              borderRadius: 8,
              background: tokens.container,
              border: `1px solid ${tokens.grid}`,
            }}
          >
            Panel surface · elevated modal uses {tokens.elevated}
          </div>
        </div>
        {!canSync && (
          <p className={styles.meta} style={{ marginTop: 12 }}>
            Account sync unlocks on Starter. Free keeps presets on this device only.
          </p>
        )}
      </div>
    </div>
  )
}

function isPreset(id: string): id is PresetThemeId {
  return (
    id === 'mechagora' ||
    id === 'midnight-slate' ||
    id === 'cream-paper' ||
    id === 'accessible-signals'
  )
}

function normalizeColorInput(hex: string): string {
  const h = ensureHash(hex)
  if (/^#[0-9A-Fa-f]{6}$/.test(h)) return h
  if (/^#[0-9A-Fa-f]{3}$/.test(h)) {
    const r = h[1]
    const g = h[2]
    const b = h[3]
    return `#${r}${r}${g}${g}${b}${b}`
  }
  return '#121620'
}

export default ThemeStudio
